<template>
  <mi-table
    ref="miTable"
    :columns="columns"
    :filter="validationsSearchString"
    :loading="isBomValidationLoading"
    :rows="userBomValidations.filteredResults"
    class="bom-validations-table full-height q-px-lg"
    column-sort-order="da"
    loading-label="Loading validations. Please wait ..."
    no-data-label="No validations found"
    row-key="id"
    title="Validations"
    :custom-header="true"
    virtual-scroll-sticky-size-start="40"
    hide-pagination
    sticky-header
    striped
    :on-row-click="openBomValidation"
    virtual-scroll
    wrap-cells
  >
    <!-- Status column -->
    <template #body-cell-status="{ value }">
      <!-- Validation status -->
      <mi-td class="bom-validations-table__status-cell">
        <div class="flex items-center">
          <man-tag v-if="value" :variant="value.color" size="m">
            {{ value.title }}
          </man-tag>
        </div>
      </mi-td>
    </template>
    <template #body-cell-actions="{ row }">
      <div class="flex actions-wrapper">
        <mi-btn
          class="action-btn"
          color="transparent"
          @click.stop
        >
          <q-icon size="small">
            <img src="@/assets/images/dots_vertical.svg" alt="" />
          </q-icon>
          <mi-menu>
            <q-list class="actions-column__list">
              <q-item
                v-close-popup
                clickable
                @click="openDeleteBomValidationDialog(row.id, row.productModelType)"
              >
                <q-item-section>
                  <q-item-label>Delete</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </mi-menu>
        </mi-btn>
        <mi-btn
          color="transparent"
          class="q-pr-lg"
        >
          <q-icon class="text-dark" size="small">
            <img src="@/assets/images/redirect_arrow.svg" alt="" />
          </q-icon>
        </mi-btn>
      </div>
    </template>
  </mi-table>

  <!-- Delete validation dialog -->
  <validation-delete-dialog
    v-model="isDeleteValidationDialogShown"
    :delete-fn="deleteBOMValidation"
    :validation-id="bomValidationIdToDelete"
    :is-user-export-delete-from-daily="isUserExport"
    @delete:validation="handleBomValidationDeletedEvent"
  ></validation-delete-dialog>
</template>

<script>
  import { createNamespacedHelpers } from 'vuex'

  import { deleteBOMValidation } from '@/api'
  import formatDate from '@/utils/formatDate'
  import { removePrefixFromPlanningPeriod } from '@/utils/planningPeriod'
  import recordAnalytics from '@/utils/analytics/recordAnalytics'

  import { RESULT_STATUS, PM_TYPE } from '@/constants'
  import {
    SET_CURRENT_BOM_VALIDATION,
    SET_IN_PROGRESS_BOM_VALIDATIONS,
    SET_BOM_VALIDATION_IS_LOADING
  } from '@/store/modules/validations/mutationTypes'
  import { BOM_VALIDATION_RESULTS_ROUTE } from '@/router/routeNames'
  import {
    BOM_UPDATE_STATUS,
    BOM_VALIDATION_DELETE,
    BOM_VALIDATIONS_PAGE
  } from '@/utils/analytics/constants'
  import getters from '@/store/modules/validations/getters'
  import ValidationDeleteDialog from '@/components/validations/ValidationDeleteDialog.vue'
  import state from '@/store/modules/validations/state'
  import recordAnalyticsMulti from '@/utils/analytics/recordAnalyticsMulti'
  import { formatProductModel, formatOverview, formatPrecondition } from '@/components/validations/utils'
  import { BOM_VALIDATIONS_OPENED } from '../../../utils/analytics/constants/validations/constants'

  const { mapActions, mapMutations, mapState } = createNamespacedHelpers('validations')

  export default {
    name: 'BomValidationsTable',
    components: { ValidationDeleteDialog },
    data: () => ({
      RESULT_STATUS,
      deleteBOMValidation,
      areBomValidationsLoading: false,
      isDeleteValidationDialogShown: false,
      isUserExport: false,
      bomValidationIdToDelete: '',
      tempBomValidationsInProgress: [],
      columns: [
        {
          name: 'componentId',
          label: 'Component',
          align: 'left',
          field: 'componentId',
          classes: 'text-uppercase',
          sortable: true,
          style: 'width: 120px'
        },
        {
          name: 'overview',
          align: 'left',
          field: 'componentVariant',
          format: formatOverview,
          style: 'width: 120px'
        },
        {
          name: 'validator',
          label: 'Validator',
          align: 'left',
          field: 'user',
          sortable: true,
          style: 'width: 120px'
        },
        {
          name: 'productModel',
          label: 'Product Model',
          align: 'left',
          field: (
            { productModelType, businessName, userExportsName } = {}
          ) => (
            { productModelType, businessName, userExportsName }
          ),
          format: formatProductModel,
          sortable: true,
          style: 'width: 300px'
        },
        {
          name: 'planningPeriodRange',
          align: 'left',
          label: 'Planning Period',
          field: ({ planningPeriodRange: { from, to } = {} } = {}) => `
            ${ removePrefixFromPlanningPeriod(from) } - ${ removePrefixFromPlanningPeriod(to) }
          `,
          sortable: true,
          style: 'width: 220px'
        },
        {
          name: 'precondition',
          align: 'left',
          label: 'Precondition',
          field: 'precondition',
          format: formatPrecondition,
          sortable: true,
          style: 'width: 140px'
        },
        {
          name: 'validationDate',
          label: 'Validation Date (Start - Finish)',
          align: 'left',
          sortable: true,
          field: ({ startDate, endDate } = {}) => ({ startDate, endDate }),
          sort: (a, b) => new Date(a.startDate) - new Date(b.startDate),
          format: ({ startDate, endDate } = {}) => `
            ${ formatDate(startDate) } - ${ endDate ? formatDate(endDate) : '' }
          `,
          style: 'width: max-content'
        },
        {
          name: 'status',
          label: 'Process Status',
          align: 'left',
          field: 'status',
          headerStyle: 'min-width: 140px; padding-left: .5rem; padding-right: .5rem',
          // eslint-disable-next-line security/detect-object-injection
          format: value => RESULT_STATUS[value] || {},
          sortable: true,
          style: 'width: 140px'
        },
        {
          name: 'actions',
          label: '',
          align: 'left',
          field: 'actions',
          headerStyle: 'min-width: 144px; padding-left: .5rem; padding-right: .5rem',
          style: 'width: 144px'
        }
      ]
    }),
    computed: {
      ...mapState([
        'bomValidations',
        'validationsSearchString',
        'isBomValidationLoading',
        'userBomValidations'
      ])
    },
    async created() {
      await this.fetchBOMValidations()
    },

    beforeMount() {
      const updateValidationHeaderEvent = new CustomEvent('validations-header', { detail: true })
      document.dispatchEvent(updateValidationHeaderEvent)
      // Analytics
      recordAnalytics(BOM_VALIDATIONS_PAGE)
    },
    mounted() {
      this.$nextTick(() => {
        this.$refs.miTable?.sort('validationDate')
      })
    },
    methods: {
      ...mapActions(['getBomValidationsList']),
      ...mapMutations({ SET_CURRENT_BOM_VALIDATION, SET_IN_PROGRESS_BOM_VALIDATIONS, SET_BOM_VALIDATION_IS_LOADING }),

      async fetchBOMValidations() {
        this.SET_BOM_VALIDATION_IS_LOADING(true)

        try {
          this.checkBomValidationsStatus()
          await this.getBomValidationsList()
        }
        finally {
          this.SET_BOM_VALIDATION_IS_LOADING(false)
        }
      },
      checkBomValidationsStatus() {
        if (!state.bomValidationsInProgress?.length) return
        const changedStatus = getters.getChangedStatus(this.tempBomValidationsInProgress)
        if (changedStatus.length) recordAnalyticsMulti(BOM_UPDATE_STATUS, changedStatus)
      },
      handleBomValidationDeletedEvent() {
        this.bomValidationIdToDelete = ''
        this.fetchBOMValidations()

        // Analytics
        recordAnalytics(BOM_VALIDATION_DELETE)
      },
      openDeleteBomValidationDialog(validationId = '', productModelType = '') {
        this.bomValidationIdToDelete = validationId
        this.isUserExport = productModelType === PM_TYPE.USER_EXPORTS
        this.isDeleteValidationDialogShown = true
      },
      openBomValidation(validation = {}) {
        this.SET_CURRENT_BOM_VALIDATION(validation)
        const route = this.$router.resolve({
          name: BOM_VALIDATION_RESULTS_ROUTE.name,
          params: { id: validation.id }
        })
        if (route?.href) {
          window.open(route.href, '_blank')

          recordAnalytics({
            name: BOM_VALIDATIONS_OPENED,
            attributes: {
              validationId: validation.id,
              hasAmbiguous: (validation.componentVariant.numberOfAmbiguousElements > 0).toString(),
              hasIncomplete: (validation.componentVariant.numberOfIncompleteElements > 0).toString()
            }
          })
        }
      }
    }
  }
</script>

<style lang="scss">
  .bom-validations-table .bom-validations-table__status-cell {
    padding-left: .5rem;
    padding-right: .5rem;
  }

  .table-cell-pm {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .actions-wrapper {
    justify-content: space-between;
  }

  .pills {
    &__pm,
    &__ue {
      min-width: 8px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
    }

    &__pm {
      background: #cbd3dc;
    }

    &__ue {
      background: #76b3de;
    }
  }

  .table-cell {
    &__text {
      word-wrap: break-word;
    }
  }

  .pills-overview-wrapper {
    display: flex;
    gap: 8px;
  }

  .pills-overview {
    &__green,
    &__red {
      display: inline-block;
      min-width: 8px;
      min-height: 8px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
    }

    &__green {
      background: #b3cd63;
    }

    &__red {
      background: #e40045;
    }
  }

  .preconditions {
    &__yes,
    &__no {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 16px;
      font-weight: 400;
    }

    &__yes {
      color: $mi-green-800;
    }
  }
</style>
